/**
 * The Bridewell "Classic" theme as code: hex-for-hex the shipped iOS palette
 * (`apps/apple/Bridewell/Theme/ColorTheme.swift`, `ColorTheme.classic`). The web exposes the same tokens as CSS
 * variables in `apps/web/src/styles/app.css` (a web test pins the two together); the Android app reads this
 * object. Colours come from tokens, never from hex at a call site, and never invent a new brand colour.
 */
export interface ThemeTokens {
  bg: string;
  bgSecondary: string;
  surface: string;
  surfaceElevated: string;
  border: string;
  text: string;
  textSecondary: string;
  textTertiary: string;
  accent: string;
  onAccent: string;
  gold: string;
  lavender: string;
  dustyBlue: string;
  blush: string;
  onPastel: string;
  success: string;
  warning: string;
  danger: string;
  hero1: string;
  hero2: string;
  hero3: string;
  onHero: string;
}

const light: ThemeTokens = {
  bg: "#faf7f5",
  bgSecondary: "#ffffff",
  surface: "#ffffff",
  surfaceElevated: "#fffcfa",
  border: "rgba(0, 0, 0, 0.08)",
  text: "#1f1721",
  textSecondary: "rgba(31, 23, 33, 0.6)",
  textTertiary: "rgba(31, 23, 33, 0.38)",
  accent: "#e94b7c",
  onAccent: "#ffffff",
  gold: "#d4af7f",
  lavender: "#b6a6e9",
  dustyBlue: "#7a9db8",
  blush: "#f8b6c5",
  onPastel: "#1f1822",
  success: "#7fccb5",
  warning: "#f2c063",
  danger: "#e76a7a",
  hero1: "#e94b7c",
  hero2: "#ff8b7b",
  hero3: "#d4af7f",
  onHero: "#2a1023",
};

const dark: ThemeTokens = {
  ...light,
  bg: "#120a17",
  bgSecondary: "#1c1224",
  surface: "#1f1429",
  surfaceElevated: "#291c36",
  border: "rgba(255, 255, 255, 0.1)",
  text: "#ffffff",
  textSecondary: "rgba(255, 255, 255, 0.62)",
  textTertiary: "rgba(255, 255, 255, 0.38)",
};

export const CLASSIC_THEME = { light, dark } as const;
export type ThemeScheme = keyof typeof CLASSIC_THEME;

/** Token name → the CSS custom property the web declares for it (`dustyBlue` → `--dusty-blue`). */
export function cssVariable(token: keyof ThemeTokens): string {
  return `--${token.replace(/[A-Z0-9]/g, (c) => `-${c.toLowerCase()}`)}`;
}
